import React from 'react';
import { Target, Users, Shield, Zap, Heart, Globe, ArrowRight } from 'lucide-react';

export function About() {
    return (
        <div className="bg-background min-h-screen">
            {/* Hero Section */}
            <section className="relative py-12 px-4 overflow-hidden">
                <div className="absolute inset-0 bg-primary/5 -skew-y-6 transform origin-top-left" />
                <div className="max-w-7xl mx-auto relative text-center">
                    <h1 className="text-4xl md:text-6xl font-black text-foreground mb-6 tracking-tight">
                        Our Mission is to <span className="text-primary italic">Automate</span> Visual Editing.
                    </h1>
                    <p className="text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                        BgRemover was founded with a simple goal: to make professional-grade image editing accessible to everyone through the power of advanced Artificial Intelligence.
                    </p>
                </div>
            </section>

            {/* Values Section */}
            <section className="py-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
                <div className="text-center mb-16">
                    <h2 className="text-3xl font-bold text-foreground">What Drives Us</h2>
                    <p className="text-muted-foreground mt-4 italic">Core values that define our culture and product.</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
                    <div className="p-8 rounded-2xl bg-card border border-border hover:border-primary/50 transition-all group">
                        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                            <Zap className="h-6 w-6 text-primary" />
                        </div>
                        <h3 className="text-xl font-bold text-foreground mb-4">Precision First</h3>
                        <p className="text-muted-foreground leading-relaxed">
                            We don't settle for "good enough". Our AI models are trained to detect even the finest hair details, ensuring a clean cut every single time.
                        </p>
                    </div>

                    <div className="p-8 rounded-2xl bg-card border border-border hover:border-primary/50 transition-all group">
                        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                            <Shield className="h-6 w-6 text-primary" />
                        </div>
                        <h3 className="text-xl font-bold text-foreground mb-4">Data Privacy</h3>
                        <p className="text-muted-foreground leading-relaxed">
                            Your images are yours. We process data securely and never store original assets longer than necessary for the editing process.
                        </p>
                    </div>

                    <div className="p-8 rounded-2xl bg-card border border-border hover:border-primary/50 transition-all group">
                        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                            <Globe className="h-6 w-6 text-primary" />
                        </div>
                        <h3 className="text-xl font-bold text-foreground mb-4">Accessibility</h3>
                        <p className="text-muted-foreground leading-relaxed">
                            Form small photographers to enterprise developers, we provide tools that scale with your needs without breaking the bank.
                        </p>
                    </div>
                </div>
            </section>

            {/* Story Section */}
            <section className="py-12 bg-card/50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-16 items-center">
                    <div>
                        <h2 className="text-3xl font-bold text-foreground mb-6">Our Journey</h2>
                        <div className="space-y-6 text-muted-foreground leading-relaxed">
                            <p>
                                Started in a small apartment in Mumbai, BgRemover was born out of the frustration of manual mask creation. We spent thousands of hours perfecting our neural networks to understand the relationship between subjects and their backgrounds.
                            </p>
                            <p>
                                Today, we serve thousands of users worldwide, processing millions of images every month with a success rate of 99.8%. We are proud to be at the forefront of AI-driven creative tools.
                            </p>
                        </div>
                        <div className="mt-8 flex gap-8">
                            <div>
                                <div className="text-3xl font-bold text-primary">10M+</div>
                                <div className="text-sm text-muted-foreground">Images Processed</div>
                            </div>
                            <div>
                                <div className="text-3xl font-bold text-primary">99.9%</div>
                                <div className="text-sm text-muted-foreground">Uptime</div>
                            </div>
                            <div>
                                <div className="text-3xl font-bold text-primary">50+</div>
                                <div className="text-sm text-muted-foreground">Countries</div>
                            </div>
                        </div>
                    </div>
                    <div className="relative">
                        <div className="aspect-square rounded-3xl overflow-hidden bg-gradient-to-tr from-primary/20 to-primary/5 border border-primary/20 flex items-center justify-center">
                            <Users className="h-32 w-32 text-primary opacity-20" />
                        </div>
                        {/* Decorative circles */}
                        <div className="absolute -top-4 -right-4 w-24 h-24 bg-primary/10 rounded-full blur-2xl" />
                        <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-primary/20 rounded-full blur-3xl" />
                    </div>
                </div>
            </section>

            {/* Partner Section */}
            <section className="py-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
                <div className="relative p-8 md:p-12 rounded-3xl bg-primary/5 border border-primary/10 overflow-hidden">
                    <div className="max-w-3xl">
                        <h2 className="text-2xl font-bold text-foreground mb-4">Part of the Barcodely Ecosystem</h2>
                        <p className="text-muted-foreground mb-6">
                            BgRemover is proud to be part of the Barcodely family, dedicated to building powerful, reliable, and lightning-fast developer tools.
                        </p>
                        <a
                            href="https://barcodely.cloud"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 text-primary font-semibold hover:underline"
                        >
                            Learn more about Barcodely.cloud <ArrowRight className="h-4 w-4" />
                        </a>
                    </div>
                </div>
            </section>
        </div>
    );
}
